"use client"

import { useEffect, useRef, useMemo, useState } from "react"
import { useAuth } from "@/contexts/auth-context"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useFeatureFlags } from "@/contexts/feature-flag-context"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { env } from '@/lib/env'

// Resolve once; guard against missing/invalid URL
const useAutomationsOrigin = () => useMemo(() => {
  try {
    return env.automationsV2Url ? new URL(env.automationsV2Url).origin : null
  } catch {
    console.error("AutomationModal: Invalid env.automationsV2Url")
    return null
  }
}, [])

/**
 * The entity-type tabs surface above the automations iframe. Each tab maps to a
 * V3 automation partition (`{tenant}-WorkflowHub-Automation-V2-{value}-Configuration[-{env}]`)
 * — selecting one scopes the rule list / editor in the external automations service
 * to that entity type. The external service reads `?entityType=` from the iframe URL
 * (graceful: it just shows WorkOrder until it learns the param). On tab change we
 * also postMessage `SET_ENTITY_TYPE` so the iframe can react without a full reload.
 *
 * Tab list is intentionally narrow — WorkOrder + the four Group C entities. Admin-y
 * types (UserProfile, Organization, Assignment, the WorkflowHubReport variants)
 * aren't authored against in the editor, so they don't get their own tab.
 */
const ENTITY_TYPE_TABS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "WorkOrder", label: "Work Orders" },
  { value: "ClientRequest", label: "Client Requests" },
  { value: "VmsRequest", label: "VMS Requests" },
  { value: "AtsPlacementRequest", label: "ATS Placements" },
  { value: "PreOnboardRequest", label: "Pre-Onboarding" },
]
const DEFAULT_ENTITY_TYPE = ENTITY_TYPE_TABS[0].value

/**
 * Appends `entityType=<value>` to the iframe URL, handling existing query strings.
 * The external automations service reads this on initial load; it's ignored on
 * services that don't know about the param (graceful fallback to WorkOrder).
 */
function buildIframeSrc(baseUrl: string | undefined, entityType: string): string | undefined {
  if (!baseUrl) return undefined
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}entityType=${encodeURIComponent(entityType)}`
}

interface AutomationModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Entity-type tab to pre-select when the modal opens. When omitted falls
   * back to <see cref="DEFAULT_ENTITY_TYPE"/> ("WorkOrder"), matching the
   * historical behaviour of the global ViewSelector kebab. Per-Request-type
   * callers (VMS tab, ATS tab, ClientRequests tab) pass the matching value
   * — `"VmsRequest"`, `"AtsPlacementRequest"`, `"ClientRequest"` — so opening
   * Automations from those tabs lands directly on the relevant rule list
   * instead of forcing the user to click through to it. Values not present
   * in <see cref="ENTITY_TYPE_TABS"/> are tolerated by falling back to the
   * default — keeps callers honest without crashing on a typo or a retired
   * entity type.
   */
  initialEntityType?: string
}

export function AutomationModal({ isOpen, onClose, initialEntityType }: AutomationModalProps) {
  const { token, handleIframeTokenRequest, sendTokenToIframe } = useAuth()
  const { configuration } = useWorkflow()
  const { isFeatureEnabled } = useFeatureFlags()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const expectedOrigin = useAutomationsOrigin()
  // Close handshake state (see requestClose below): the iframe ACKs a close request, then sends
  // CLOSE_CONFIRMED once it's safe to close. A fallback timer closes anyway if it never ACKs.
  const closeAckRef = useRef(false)
  const closeFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (closeFallbackTimerRef.current) clearTimeout(closeFallbackTimerRef.current)
  }, [])

  // Each Group C entity is behind a feature flag (same gates the hub tabs use). A tenant
  // without a feature shouldn't see its automations tab. WorkOrder is the always-on base.
  // PreOnboard has no dedicated flag — it's the client-request onboarding stage, so it rides
  // the ClientRequest flag.
  const visibleEntityTabs = useMemo(
    () => ENTITY_TYPE_TABS.filter(({ value }) => {
      switch (value) {
        case "ClientRequest": return isFeatureEnabled("clientRequestReview")
        case "VmsRequest": return isFeatureEnabled("vmsIntegration")
        case "AtsPlacementRequest": return isFeatureEnabled("atsPlacementReview")
        default: return true // WorkOrder — base, always available
      }
    }),
    [isFeatureEnabled],
  )

  // Resolve the caller's requested tab against the VISIBLE list. An unknown or
  // feature-gated value falls back to the first visible tab (WorkOrder is always
  // present), so we never land on a hidden/absent tab.
  const resolveInitialTab = (requested: string | undefined): string => {
    const fallback = visibleEntityTabs[0]?.value ?? DEFAULT_ENTITY_TYPE
    if (!requested) return fallback
    return visibleEntityTabs.some(t => t.value === requested) ? requested : fallback
  }
  const [selectedEntityType, setSelectedEntityType] = useState<string>(() => resolveInitialTab(initialEntityType))

  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasAutomationsLinkPermission = authorizedActions.includes("AutomationsLink")

  // Re-seed the selected tab whenever the modal reopens. Caller controls
  // which tab via initialEntityType — without this, a user who closed the
  // modal mid-edit and reopened from a different tab would still see the
  // last-edited tab instead of the one they just clicked from. When the
  // caller omits the prop (ViewSelector / WorkOrder-tab path) this honours
  // the legacy "always default to WorkOrder" behaviour.
  useEffect(() => {
    if (isOpen) setSelectedEntityType(resolveInitialTab(initialEntityType))
  }, [isOpen, initialEntityType])

  // Notify the iframe on tab change. The src-rebuild below also triggers a full
  // reload (browser default when iframe src changes) so the external service
  // re-fetches the rule list scoped to the new EntityType. The postMessage is
  // an optional fast-path for an external service that wants to update in-place
  // without a reload — services that don't listen for SET_ENTITY_TYPE just
  // ignore the message and let the reload do the work.
  useEffect(() => {
    if (!isOpen || !iframeRef.current?.contentWindow || !expectedOrigin) return
    iframeRef.current.contentWindow.postMessage(
      { action: "SET_ENTITY_TYPE", entityType: selectedEntityType },
      expectedOrigin,
    )
  }, [selectedEntityType, isOpen, expectedOrigin])

  useEffect(() => {
    if (!isOpen || !hasAutomationsLinkPermission) return

    const handleMessage = (event: MessageEvent) => {
      try {
        // Validate the message origin AND that it came from THIS iframe — not just any window on the
        // automations origin (which could otherwise send CLOSE_CONFIRMED to discard unsaved work, or
        // CLOSE_ACK to disable the fallback).
        if (!expectedOrigin) {
          console.warn("AutomationModal: Cannot validate origin - invalid automations URL")
          return
        }
        if (event.origin !== expectedOrigin || event.source !== iframeRef.current?.contentWindow) {
          console.warn("Ignoring message from unexpected origin/source:", event.origin)
          return
        }

        const data = event.data

        // Handle REQUEST_TOKEN message from iframe
        if (data && data.action === "REQUEST_TOKEN" && iframeRef.current?.contentWindow && expectedOrigin) {
          // Let the auth context handle the token request (bridge logic)
          handleIframeTokenRequest(iframeRef.current.contentWindow, expectedOrigin)
        }

        // Close handshake replies from the iframe (see requestClose).
        if (data && data.action === "CLOSE_ACK") {
          // The iframe heard us and owns the flow now — cancel our fallback and wait for CONFIRMED.
          closeAckRef.current = true
          if (closeFallbackTimerRef.current) clearTimeout(closeFallbackTimerRef.current)
        }
        if (data && data.action === "CLOSE_CONFIRMED") {
          if (closeFallbackTimerRef.current) clearTimeout(closeFallbackTimerRef.current)
          onClose()
        }
      } catch (error) {
        console.error("AutomationModal: Error processing message from iframe:", error)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
      }
    }
  }, [isOpen, handleIframeTokenRequest, expectedOrigin, onClose])

  // Send token to iframe when token changes and iframe is loaded
  useEffect(() => {
    if (isOpen && hasAutomationsLinkPermission && token && iframeRef.current?.contentWindow && expectedOrigin) {
      const timer = setTimeout(() => {
        sendTokenToIframe(iframeRef.current!.contentWindow!, expectedOrigin)
      }, 500) // Small delay to ensure iframe is ready

      return () => clearTimeout(timer)
    }
  }, [isOpen, hasAutomationsLinkPermission, token, expectedOrigin, sendTokenToIframe])

  // Guarded close: the iframe owns the unsaved-changes prompt (only it knows its dirty state), so
  // ask it to close rather than tearing it down outright. It ACKs immediately, then sends
  // CLOSE_CONFIRMED once safe (right away when clean, or after the user confirms discarding edits).
  // If the embedded build is older and never ACKs, the fallback timer closes anyway.
  const requestClose = () => {
    const frame = iframeRef.current?.contentWindow
    if (!frame || !expectedOrigin) {
      onClose()
      return
    }
    closeAckRef.current = false
    if (closeFallbackTimerRef.current) clearTimeout(closeFallbackTimerRef.current)
    frame.postMessage({ action: "REQUEST_CLOSE" }, expectedOrigin)
    closeFallbackTimerRef.current = setTimeout(() => {
      if (!closeAckRef.current) onClose()
    }, 500)
  }

  const handleIframeLoad = () => {
    // Send token when iframe loads
    if (hasAutomationsLinkPermission && token && iframeRef.current?.contentWindow && expectedOrigin) {
      sendTokenToIframe(iframeRef.current.contentWindow, expectedOrigin)
    }
  }

  const iframeSrc = useMemo(
    () => buildIframeSrc(env.automationsV2Url, selectedEntityType),
    [selectedEntityType],
  )

  if (!isOpen || !hasAutomationsLinkPermission) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header bar: entity-type tabs + close button. Fixed height keeps the
          iframe taking the rest of the viewport cleanly. */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2 shadow-sm">
        <Tabs value={selectedEntityType} onValueChange={setSelectedEntityType}>
          {/* Bigger + colored entity tabs — default shadcn styling was too easy
              for new users to miss (light gray text on white). Active-tab fill
              is the same peach (rgb(255,169,108) / #FFA96C) used by the workflow
              hub stage pills, so the tab strip reads as part of the same control
              vocabulary as the stage selector rather than a one-off accent. */}
          <TabsList className="h-10 gap-1 bg-gray-100 p-1">
            {visibleEntityTabs.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="px-4 text-sm font-medium text-gray-700 data-[state=active]:bg-[#FFA96C] data-[state=active]:text-gray-900 data-[state=active]:shadow-sm hover:text-gray-900"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <button
          onClick={requestClose}
          className="p-2 bg-white hover:bg-gray-100 rounded-full shadow-sm transition-colors border border-gray-200"
          aria-label="Close"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Iframe content — fills remaining viewport. Key on the entity type so
          changing the tab fully remounts the iframe; cheaper than juggling the
          external service's internal state on tab change, and the auth token
          is re-sent on the new mount's load event. */}
      <iframe
        key={selectedEntityType}
        ref={iframeRef}
        src={iframeSrc}
        className="flex-1 w-full border-0"
        onLoad={handleIframeLoad}
        title="Automations"
      />
    </div>
  )
}
